/**
 * Authentication provider module that manages user authentication state using Microsoft Authentication Library (MSAL).
 * Provides a context-based authentication system for the application.
 */

import { createContext, useContext, useState } from "react";
import { MsalProvider } from "@azure/msal-react";
import PropTypes from "prop-types";

/**
 * Authentication context that stores the current user state and methods to update it.
 * @type {React.Context<{user: Object|null, setUser: Function}>}
 */
const AuthContext = createContext({
  user: null,
  setUser: () => {},
});

/**
 * Custom hook to access the authentication context.
 * @returns {Object} The authentication context containing user state and setter
 * @throws {Error} If used outside of AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

/**
 * Authentication provider component that wraps the application with MSAL authentication.
 * Provides user state management and authentication context to child components.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to be wrapped
 * @param {Object} props.instance - MSAL instance for authentication
 * @returns {JSX.Element} The rendered AuthProvider component
 */
export const AuthProvider = ({ children, instance }) => {
  const [user, setUser] = useState(null);

  const authValue = {
    user,
    setUser,
  };

  return (
    <MsalProvider instance={instance}>
      <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>
    </MsalProvider>
  );
};

// PropTypes validation for AuthProvider component
AuthProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.element,
  ]).isRequired,
  instance: PropTypes.shape({
    initialize: PropTypes.func,
    handleRedirectPromise: PropTypes.func,
    loginPopup: PropTypes.func,
    loginRedirect: PropTypes.func,
    logout: PropTypes.func,
  }).isRequired,
};
